import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart, BookOpen, HelpCircle, Video, MapPin, ChevronRight, Download, Monitor, Tablet, Smartphone, Mail, Phone, Globe } from 'lucide-react';
import { triggerInstall, onInstallAvailable } from './PWAInstallPrompt';

const navLinks = [
  { to: '/patients', label: 'Accueil', icon: null },
  { to: '/patients/categorie/conseils', label: 'Conseils', icon: Heart },
  { to: '/patients/categorie/en-savoir-plus', label: 'En savoir +', icon: BookOpen },
  { to: '/patients/categorie/tutoriels-videos', label: 'Tutoriels', icon: Video },
  { to: '/patients/faq', label: 'FAQ', icon: HelpCircle },
];

const PatientLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const location = useLocation();
  const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const showPWA = !isStandalone;

  useEffect(() => {
    const unsub = onInstallAvailable((available) => setInstallAvailable(available));
    return unsub;
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    if (installAvailable) await triggerInstall('patient');
    setTimeout(() => setInstalling(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ fontFamily: "'Roboto', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/patients" className="flex items-center gap-2.5 group">
            <img src="/SFOALC-logo-site.png" alt="SFOALC" className="h-8 w-auto" />
            <div className="hidden sm:block">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none">Espace</span>
              <span className="block text-sm font-bold text-[#1e3a5f] leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>Patients</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {navLinks.map(link => {
              const isActive = location.pathname === link.to || (link.to !== '/patients' && location.pathname.startsWith(link.to));
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    isActive
                      ? 'text-[#1e3a5f] bg-[#eaf6fc]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {Icon && <Icon size={13} className={isActive ? 'text-[#35AFE4]' : 'text-gray-400'} />}
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex text-[10px] text-gray-400 hover:text-gray-600 transition-colors">
              Site principal
            </Link>
            <Link to="/annuaire" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#1e3a5f] text-white rounded-full text-xs font-bold hover:bg-[#162d4a] transition-all hover:shadow-md" data-testid="find-doctor-btn">
              <MapPin size={12} /> Praticien
            </Link>
            {/* Mobile menu */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1.5 text-gray-600 hover:text-gray-900" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg animate-in slide-in-from-top-2">
            <nav className="max-w-6xl mx-auto px-4 py-3 space-y-0.5">
              {navLinks.map(link => {
                const isActive = location.pathname === link.to;
                const Icon = link.icon;
                return (
                  <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'bg-[#eaf6fc] text-[#1e3a5f]' : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                    {Icon && <Icon size={16} className={isActive ? 'text-[#35AFE4]' : 'text-gray-400'} />}
                    {link.label}
                    <ChevronRight size={14} className="ml-auto text-gray-300" />
                  </Link>
                );
              })}
              <Link to="/annuaire" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-sm font-medium text-[#1e3a5f] bg-[#eaf6fc] mt-2">
                <MapPin size={16} className="text-[#35AFE4]" /> Trouver un praticien
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#1e2a3a] text-white py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/SFOALC-footer-logo.png" alt="SFOALC" className="h-12 mb-3" />
              <p className="text-white/60 text-xs leading-relaxed">
                Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact
              </p>
              <a href="mailto:contact@sfo-alc.fr" className="flex items-center gap-1.5 text-white/40 hover:text-white text-xs mt-3 transition-colors">
                <Mail size={12} /> contact@sfo-alc.fr
              </a>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Espace Patients</h3>
              <div className="space-y-1.5 text-xs">
                <Link to="/patients/categorie/conseils" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Heart size={13} className="opacity-50" /> Conseils d'utilisation</Link>
                <Link to="/patients/categorie/tutoriels-videos" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Video size={13} className="opacity-50" /> Tutoriels vidéos</Link>
                <Link to="/patients/faq" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><HelpCircle size={13} className="opacity-50" /> Foire aux questions</Link>
                <Link to="/patients/categorie/en-savoir-plus" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><BookOpen size={13} className="opacity-50" /> En savoir plus</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>L'association</h3>
              <div className="space-y-1.5 text-xs mb-4">
                <Link to="/annuaire" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><MapPin size={13} className="opacity-50" /> Annuaire des praticiens</Link>
                <Link to="/" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Globe size={13} className="opacity-50" /> Site principal</Link>
                <Link to="/contact" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Phone size={13} className="opacity-50" /> Contact</Link>
              </div>

              {/* Donate heart button */}
              <a href="https://www.helloasso.com/associations/sfoalc-societe-francaise-des-ophtalmologistes-adaptateurs-de-lentilles-de-contact" target="_blank" rel="noopener noreferrer"
                className="group flex items-center gap-2.5 mb-5">
                <div className="relative flex items-center justify-center w-8 h-8">
                  <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                  <span className="absolute inset-0.5 rounded-full bg-rose-500/10 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.3s' }} />
                  <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:scale-110 transition-transform">
                    <Heart size={14} className="text-white fill-white" />
                  </div>
                </div>
                <div>
                  <p className="text-rose-300 text-xs font-bold group-hover:text-rose-200 transition-colors">Soutenir l'association</p>
                  <p className="text-white/40 text-[10px]">Faire un don</p>
                </div>
              </a>

              {/* PWA Install — animated icon */}
              {showPWA && (
                <button onClick={handleInstall}
                  className="group w-full"
                  data-testid="patient-pwa-install-btn">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-full border-2 transition-all duration-500 ${installing ? 'border-[#35AFE4] bg-[#35AFE4]/20' : 'border-[#35AFE4]/30 bg-[#35AFE4]/5 hover:border-[#35AFE4]/70 hover:bg-[#35AFE4]/15'}`}>
                    <div className={`w-9 h-9 rounded-full bg-[#35AFE4] flex items-center justify-center flex-shrink-0 transition-all duration-500 ${installing ? 'scale-90' : 'group-hover:scale-110 group-hover:shadow-[0_0_16px_rgba(58,143,183,0.5)]'}`}>
                      <Download size={16} className="text-white animate-[downloadBounce_2s_ease-in-out_infinite]" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-white text-[13px] font-bold leading-tight">
                        {installing ? 'Installation...' : "Installer l'app"}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Monitor size={11} className="text-white/50" />
                        <Tablet size={11} className="text-white/50" />
                        <Smartphone size={11} className="text-white/50" />
                        <span className="text-white/50 text-[11px]">iOS / Android / PC</span>
                      </div>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-white/30 text-[10px]">&copy; {new Date().getFullYear()} SFOALC — Tous droits réservés</p>
            <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 group">
              <span className="text-white/30 text-[10px] group-hover:text-white/50 transition-colors">Site et application développés par</span>
              <img src="/logo-agence-msc.webp" alt="Agence MSC" className="h-3.5 w-auto opacity-50 group-hover:opacity-90 transition-opacity" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PatientLayout;
